import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, Plus, Cpu, AlertTriangle, Edit, Trash2 } from "lucide-react";

const PartsPage = () => {
  const [parts, setParts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({
    reference: "",
    designation: "",
    type_appareil_compatible: [],
    marque_compatible: [],
    fournisseur: "",
    cout_achat: 0,
    prix_vente: 0,
    quantite_stock: 0,
    seuil_alerte: 5
  });

  const fetchParts = async () => {
    setLoading(true);
    try {
      const params = { search, low_stock: lowStockOnly, limit: 200 };
      const response = await API.get("/parts", { params });
      setParts(response.data.parts);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchParts, 300);
    return () => clearTimeout(timer);
  }, [search, lowStockOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPart) {
        await API.put(`/parts/${editingPart.id}`, formData);
        toast.success("Pièce mise à jour");
      } else {
        await API.post("/parts", formData);
        toast.success("Pièce créée");
      }
      setDialogOpen(false);
      resetForm();
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer cette pièce ?")) return;
    try {
      await API.delete(`/parts/${id}`);
      toast.success("Pièce supprimée");
      fetchParts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const openEditDialog = (part) => {
    setEditingPart(part);
    setFormData({
      reference: part.reference,
      designation: part.designation,
      type_appareil_compatible: part.type_appareil_compatible || [],
      marque_compatible: part.marque_compatible || [],
      fournisseur: part.fournisseur || "",
      cout_achat: part.cout_achat,
      prix_vente: part.prix_vente,
      quantite_stock: part.quantite_stock,
      seuil_alerte: part.seuil_alerte
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPart(null);
    setFormData({
      reference: "",
      designation: "",
      type_appareil_compatible: [],
      marque_compatible: [],
      fournisseur: "",
      cout_achat: 0,
      prix_vente: 0,
      quantite_stock: 0,
      seuil_alerte: 5
    });
  };

  const isLowStock = (part) => part.quantite_stock <= part.seuil_alerte;

  return (
    <div className="space-y-6" data-testid="parts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Pièces SAV</h1>
          <p className="text-muted-foreground">{total} références</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-part-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle pièce
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingPart ? "Modifier la pièce" : "Nouvelle pièce"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Référence *</Label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      required
                      data-testid="part-reference-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fournisseur</Label>
                    <Input
                      value={formData.fournisseur}
                      onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Désignation *</Label>
                  <Input
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    required
                    data-testid="part-designation-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Coût d'achat (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.cout_achat}
                      onChange={(e) => setFormData({ ...formData, cout_achat: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix de vente (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.prix_vente}
                      onChange={(e) => setFormData({ ...formData, prix_vente: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantité en stock</Label>
                    <Input
                      type="number"
                      value={formData.quantite_stock}
                      onChange={(e) => setFormData({ ...formData, quantite_stock: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Seuil d'alerte</Label>
                    <Input
                      type="number"
                      value={formData.seuil_alerte}
                      onChange={(e) => setFormData({ ...formData, seuil_alerte: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="part-submit-btn">
                  {editingPart ? "Enregistrer" : "Créer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par référence ou désignation..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="part-search-input"
              />
            </div>
            <Button
              variant={lowStockOnly ? "default" : "outline"}
              onClick={() => setLowStockOnly(!lowStockOnly)}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Stock faible
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : parts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <Cpu className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucune pièce trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Référence</TableHead>
                    <TableHead>Désignation</TableHead>
                    <TableHead className="hidden md:table-cell">Fournisseur</TableHead>
                    <TableHead className="text-right">Coût</TableHead>
                    <TableHead className="text-right">Prix vente</TableHead>
                    <TableHead className="text-center">Stock</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parts.map((part) => (
                    <TableRow key={part.id} data-testid={`part-row-${part.id}`}>
                      <TableCell className="font-mono font-medium">{part.reference}</TableCell>
                      <TableCell>{part.designation}</TableCell>
                      <TableCell className="hidden md:table-cell">{part.fournisseur || "-"}</TableCell>
                      <TableCell className="text-right font-mono">{part.cout_achat?.toFixed(2)}€</TableCell>
                      <TableCell className="text-right font-mono">{part.prix_vente?.toFixed(2)}€</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={isLowStock(part) ? "destructive" : "secondary"}>
                          {isLowStock(part) && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {part.quantite_stock}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(part)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(part.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartsPage;
